import { useActiveElement, useEventListener, useMagicKeys, whenever } from "@vueuse/core";
import { and, or } from "@vueuse/math";
import { computed, watchEffect } from "vue";

type KeysOptions = {
	/** Will prevent event propagation if the keys are active */
	prevent?: boolean;
	disableInputCheck?: boolean;
};

/**
 * Helper composable to listen to keys combination.
 * The callback is not fired if the focus is on an input or textarea element.
 * @param combinations The keys combination (ex: 'b', 'Ctrl+k')
 * @param fn The callback function to call
 * @param options
 */
export function useKeys(
	combinations: string | string[],
	fn: () => void,
	options: KeysOptions = {}
) {
	const keys = useMagicKeys();
	const activeElement = useActiveElement();
	const notOnInput = computed(
		() =>
			options.disableInputCheck ||
			(activeElement.value?.tagName !== "INPUT" &&
				activeElement.value?.tagName !== "TEXTAREA" &&
				activeElement.value?.tagName !== "BUTTON")
	);

	let preventNextEvent = false;

	const keysCombination = Array.isArray(combinations)
		? combinations.map((c) => keys[c])
		: [keys[combinations]];

	const keysActive = and(notOnInput, or(...keysCombination));

	watchEffect(
		() => {
			if (keysActive.value && options.prevent) {
				preventNextEvent = true;
			}
		},
		{ flush: "sync" }
	);

	useEventListener("keydown", (e) => {
		if (preventNextEvent) {
			e.preventDefault();
		}

		preventNextEvent = false;
	});

	whenever(keysActive, fn);
}
